import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import AddressFields from 'components/Customer/AddressFields';
import AddressType from 'components/Customer/CustomerAddressType';
import { connect } from 'react-redux';
import { change, FormSection, formValueSelector } from 'redux-form';
import AddCustomerAddressModal from 'components/Customer/AddCustomerAddressModal';
import ConfirmModal from 'components/Modals/ConfirmModal';

import {
  openAddCustomerAddressesModal,
  closeAddCustomerAddressesModal,
  changePrimaryAddress
} from 'actions';

const CustomerPostalAddress = ({ mailingAddresses, permAddresses, isEditingCustomer, jurisdictions, dispatch,
  isAddCustomerAddressModalShown, dataSource
}) => {
  const [toggle, setToggle] = useState(true);
  const [modalAddressType, setModalAddressType] = useState(null);
  const [showMakePrimaryConfirmModal, setShowMakePrimaryConfirmModal] = useState(false);
  const [addressIndex, setAddressIndex] = useState(0);

  const handleSetToggle = () => {
    setToggle(prevState => !prevState);
  };

  const showAddAddressModal = (addressType) => {
    setModalAddressType(addressType);
    dispatch(openAddCustomerAddressesModal());
  };
  const closeAddAddressModal = () => dispatch(closeAddCustomerAddressesModal());

  const localPermAddresses = [...permAddresses || []];
  const localMailingAddresses = [...mailingAddresses || []];

  const handleRemoveAddress = (addressType, index) => {
    switch (addressType) {
      case AddressType.permanent:
        localPermAddresses.splice(index, 1);
        dispatch(change('customerEditForm', 'permanentAddresses', localPermAddresses));
        break;
      case AddressType.mailing:
        localMailingAddresses.splice(index, 1);
        dispatch(change('customerEditForm', 'mailingAddresses', localMailingAddresses));
        break;
      default:
    }
  };

  const handleChangePermAddressRegime = (index, taxRegime) => {
    for (let i = 0; i < localPermAddresses.length; i += 1) {
      const regime = localPermAddresses[i]?.regime;

      if (index === i) {
        dispatch(
          change('customerEditForm', `${ 'permanentAddresses' }[${ i }].regime`, regime | taxRegime)
        );
      } else {
        dispatch(
          // eslint-disable-next-line no-bitwise
          change('customerEditForm', `${ 'permanentAddresses' }[${ i }].regime`, regime & ~taxRegime)
        );
      }
    }
  };

  const onSubmitMakePrimary = () => {
    dispatch(changePrimaryAddress(localMailingAddresses[addressIndex]?.customerId, localMailingAddresses[addressIndex]?.id));
    setShowMakePrimaryConfirmModal(false);
  };

  const handleMakePrimaryMailingClick = (index) => {
    if (dataSource === 'form') {
      setAddressIndex(index);
      setShowMakePrimaryConfirmModal(true);
      return;
    }

    if (isEditingCustomer) {
      for (let i = 0; i < localMailingAddresses.length; i += 1) {
        dispatch(
          change('customerEditForm', `${ 'mailingAddresses' }[${ i }].isPrimary`, i === index)
        );
      }
    }
  };

  const handleMakePrimaryPermClick = (index) => {
    if (isEditingCustomer) {
      for (let i = 0; i < localPermAddresses.length; i += 1) {
        dispatch(
          change('customerEditForm', `${ 'permanentAddresses' }[${ i }].isPrimary`, i === index)
        );
      }
    }
  };

  const hasPermAddresses = 2 && localPermAddresses.length > 0;
  const hasMailingAddresses = localMailingAddresses.length > 0;

  return (
    <div className="customer-collapsible-panel">
      <div className="inline-header-dropdown">
        { (hasPermAddresses || hasMailingAddresses)
        && <button
          type="button"
          className="btn panel-data-toggle-button"
          onClick={ handleSetToggle }
          title={ `${ toggle ? 'Hide' : 'Show' } Addresses` }
          id="customer-addresses-toggle"
        >
          <span className={ `icon icon-arrow-${ toggle ? 'up' : 'down' }` } />
        </button>}
        <h2 id="customer-addresses-header"><strong>Addresses:</strong></h2>
        {isEditingCustomer && <button
          type="button"
          className="btn btn-add-address"
          onClick={ () => showAddAddressModal(AddressType.permanent) }
          disabled={ localPermAddresses.length > 1 }
          title={ localPermAddresses.length > 1 ? 'You can add up to 2 residence addresses' : 'Add residence address' }
          id="add-perm-address-button"
        >+ Add Residence
        </button>}
        {isEditingCustomer && <button
          type="button"
          className="btn btn-add-address"
          onClick={ () => showAddAddressModal(AddressType.mailing) }
          disabled={ localMailingAddresses.length > 9 }
          title={ localMailingAddresses.length > 9 ? 'You can add up to 2 mailing addresses' : 'Add mailing address' }
          id="add-mailing-address-button"
        >+ Add Mailing
        </button>}
      </div>
      <div className={ toggle ? 'data-grid' : 'hide' } id="customer-addresses-data">
        {localPermAddresses?.map((address, index) => (address
        && <div key={ `permAddress-${ index + 1 }` }>
          <FormSection
            name={ `permanentAddresses[${ index }]` }
            component={ AddressFields }
            addressProps={ address?.address }
            isEditable={ isEditingCustomer }
            index={ index }
            addressType={ AddressType.permanent }
            isPrimary={ address?.isPrimary }
            regime={ address?.regime }
            jurisdictions={ jurisdictions }
            dispatch={ dispatch }
            onRemoveAddress={ handleRemoveAddress }
            handleChangeAddressRegime={ handleChangePermAddressRegime }
            onMakePrimaryClick={ handleMakePrimaryPermClick }
          />
        </div>
        ))}
        {localMailingAddresses?.map((address, index) => (address
        && <div key={ `mailingAddress-${ index + 1 }` }>
          <FormSection
            name={ `mailingAddresses[${ index }]` }
            component={ AddressFields }
            addressProps={ address?.address }
            isEditable={ isEditingCustomer }
            index={ index }
            addressType={ AddressType.mailing }
            isPrimary={ address?.isPrimary }
            regime={ address?.regime }
            jurisdictions={ jurisdictions }
            dispatch={ dispatch }
            onRemoveAddress={ handleRemoveAddress }
            onMakePrimaryClick={ handleMakePrimaryMailingClick }
            isMailingPrimaryEditable={ dataSource === 'form' }
          />
        </div>
        ))}
      </div>
      {isAddCustomerAddressModalShown && <AddCustomerAddressModal
        onCancel={ closeAddAddressModal }
        isAddAddressModalShown={ isAddCustomerAddressModalShown }
        addressType={ modalAddressType }
        prevAddresses={ modalAddressType === AddressType.permanent ? localPermAddresses : localMailingAddresses }
        jurisdictions={ jurisdictions }
        isPrimary={ (modalAddressType === AddressType.permanent && localPermAddresses.length === 0)
                 || (modalAddressType === AddressType.mailing && localMailingAddresses.length === 0) }
      />}
      {showMakePrimaryConfirmModal && <ConfirmModal
        key="confirmMakePrimaryMailing"
        isShown={ showMakePrimaryConfirmModal }
        onConfirm={ () => onSubmitMakePrimary() }
        onCancel={ () => setShowMakePrimaryConfirmModal(false) }
        title="Switch Primary Mailing Address"
        message="Are you sure you want to switch the Primary Mailing Address?"
      />}
    </div>
  );
};

CustomerPostalAddress.propTypes = {
  mailingAddresses: PropTypes.arrayOf(PropTypes.shape()),
  permAddresses: PropTypes.arrayOf(PropTypes.shape()),
  isEditingCustomer: PropTypes.bool,
  jurisdictions: PropTypes.arrayOf(PropTypes.shape()),
  dispatch: PropTypes.func.isRequired,
  isAddCustomerAddressModalShown: PropTypes.bool,
  dataSource: PropTypes.string.isRequired
};

CustomerPostalAddress.defaultProps = {
  mailingAddresses: null,
  permAddresses: null,
  jurisdictions: null,
  isEditingCustomer: false,
  isAddCustomerAddressModalShown: false
};

const selector = formValueSelector('customerEditForm');

const mapStateToProps = state => ({
  permAddresses: selector(state, 'permanentAddresses'),
  mailingAddresses: selector(state, 'mailingAddresses'),
  isAddCustomerAddressModalShown: state?.customer?.isAddCustomerAddressModalShown
});

export default connect(mapStateToProps)(CustomerPostalAddress);
